import React, { useState, JSX, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import {
  ChevronDown,
  ChevronRight,
  CircleX,
  Download,
  FileText,
  Folder,
  Grid,
  List,
} from "lucide-react";
import { useGetPreSignedURL } from "../../queries/onboarding";

export type FileTree = {
  [key: string]: "file" | FileTree;
};

interface FileExplorerProps {
  data: FileTree;
  cin: string;
  onFileSelect: (file: string | null) => void;
  onClose: () => void;
}

export const FileExplorer: React.FC<FileExplorerProps> = ({
  data,
  cin,
  onFileSelect,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [isGrid, setIsGrid] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [isSignedUrl, setIsSignedUrl] = useState<string>();
  const { data: signedUrl, refetch } = useGetPreSignedURL(
    isSignedUrl || "",
    isSignedUrl !== undefined,
  );

  const handleFileSelect = useCallback(
    (file: string | null) => {
      setSelectedFile(file);
      onFileSelect(file);
    },
    [onFileSelect],
  );

  const toggleFolder = (path: string) => {
    const newSet = new Set(expanded);
    if (newSet.has(path)) {
      newSet.delete(path);
    } else {
      newSet.add(path);
    }
    setExpanded(newSet);
  };

  useEffect(() => {
    if (!isSignedUrl) return;

    const fetchSignedUrl = async () => {
      try {
        const result = await refetch();
        if (result.data) {
          window.open(result.data, "_blank");
        }
      } catch (error) {
        toast("Error Downloading File", { type: "error", autoClose: 2000 });
      }
    };

    fetchSignedUrl();
  }, [isSignedUrl]);

  const matchesSearch = (key: string) => {
    return key.toLowerCase().includes(searchTerm.toLowerCase());
  };

  const renderTree = (tree: FileTree, path = ""): JSX.Element[] => {
    return Object.entries(tree).flatMap(([key, value]) => {
      const currentPath = key;
      const name = key.split("/").pop() || key;

      // Check if the value is a file (not a FolderTree)
      if (value === "file" && name.endsWith(".pdf")) {
        return matchesSearch(name) ? (
          <div
            key={currentPath}
            className={`pl-6 flex items-center gap-2 text-gray-700 border-b border-dashed border-b-gray-200 py-1 cursor-pointer${
              isGrid ? "grid-item" : ""
            } ${selectedFile === currentPath ? "bg-blue-300" : "hover:bg-gray-50"}`}
            onClick={() => handleFileSelect(currentPath)}
            onDoubleClick={() => {
              onFileSelect(currentPath);
              onClose();
            }}
          >
            <Download
              className="w-4 h-4 text-blue-800 flex-shrink-0 hover:bg-blue-100 rounded-lg"
              onClick={() => {
                setIsSignedUrl(`${cin}/${key}`);
              }}
            />
            <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span className="text-sm truncate flex-1" title={name}>
              {name}
            </span>
          </div>
        ) : (
          []
        );
      }

      // Folder logic (only call renderTree on folders, not files)
      if (typeof value === "object") {
        const children = renderTree(value, currentPath);
        const shouldShow = matchesSearch(name) || children.length > 0;
        const isOpen = expanded.has(currentPath);

        if (!shouldShow) return [];

        return (
          <div
            key={currentPath}
            className={`pl-4 pr-2 ${isGrid ? "grid-item" : ""}`}
          >
            <div
              className="flex items-center gap-1 cursor-pointer text-gray-800 hover:text-blue-600 pb-1"
              onClick={() => toggleFolder(currentPath)}
            >
              {isOpen ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              <Folder className="w-4 h-4 text-yellow-500" />
              <span className="font-medium text-sm">{name}</span>
            </div>
            {isOpen && <div className="ml-4 mt-1">{children}</div>}
          </div>
        );
      }

      return [];
    });
  };

  return (
    <div className="w-full p-4 h-[425px] bg-white shadow rounded border border-gray-200 flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <div className="relative w-full">
          <input
            type="text"
            placeholder="Search files or folders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              <CircleX className="w-4 h-4 hover:text-blue-700" />
            </button>
          )}
        </div>

        <button
          onClick={() => setIsGrid(!isGrid)}
          className="ml-2 p-2 rounded hover:bg-gray-100"
        >
          {isGrid ? <List className="w-5 h-5" /> : <Grid className="w-5 h-5" />}
        </button>
      </div>

      <div
        className={`text-sm text-gray-800 overflow-y-auto flex-1 ${isGrid ? "grid grid-cols-2 gap-2" : ""}`}
      >
        {renderTree(data)}
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <button
          onClick={onClose}
          className="px-4 py-1 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
        >
          Cancel
        </button>
        <button
          disabled={!selectedFile}
          onClick={() => {
            onFileSelect(selectedFile);
            onClose();
          }}
          className={`px-4 py-1 text-sm text-white ${!selectedFile ? "cursor-not-allowed bg-blue-300" : "cursor-pointer bg-blue-600 hover:bg-blue-700"}  rounded `}
        >
          Select
        </button>
      </div>
    </div>
  );
};

export default FileExplorer;
